import React, { useEffect, useState } from "react";
import { ICreateSalesData } from "../../../../@types";
import { CloseIcon } from "../../../../assets/icons";
import ArrowLeft from "../../../../assets/icons/ArrowLeft";
import ArrowRight from "../../../../assets/icons/ArrowRight";
import { getBankAccounts } from "../../../../backend-services";
import {
  REOCCURRING_FREQUENCY,
  REOCCURRING_REMINDER,
} from "../../../../constants";
import { InvoiceErrors } from "../../../../modules/validateInvoiceForm";
import { useUserStore } from "../../../../state";
import { BankData } from "../../../../types";
import { DropdownComponent, InputComponent } from "../../../core/Forms";
import { useCompanyStore } from "../../../../state/useCompanyStore";

interface InvoiceRecurringProps {
  handlePaymentLink: () => void;
  handleDropDownChange: (name: string, value: string) => void;
  formData: ICreateSalesData;
  setFormData: React.Dispatch<React.SetStateAction<ICreateSalesData>>;
  handleInputChange: (event: { target: { name: any; value: any } }) => void;
  handleSave: () => void;
  closeForm: () => void;
  validationResults: InvoiceErrors;
  setValidationResults: React.Dispatch<React.SetStateAction<InvoiceErrors>>;
}

const RecurringPrompt: React.FC<InvoiceRecurringProps> = ({
  formData,
  setFormData,
  handleDropDownChange,
  handlePaymentLink,
  handleInputChange,
  // handleSave,
  closeForm,
  validationResults,
  setValidationResults,
}) => {
  const dropDownRepeatedly = REOCCURRING_FREQUENCY;
  const dropDownReminder = REOCCURRING_REMINDER;
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [endDate, setEndDate] = useState("");
  const [selectedAccount, setSelectedAccount] = useState<string>("");
  const [selectedAccountLabel, setSelectedAccountLabel] = useState<string>("");
  const [bankAccounts, setBankAccounts] = useState<BankData[]>([]);
  const [error, setError] = useState<string>("");
  const user = useCompanyStore((state) => state.company);
  const bankAccount = user.bankAccounts || [];

  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const accounts = await getBankAccounts();
        setBankAccounts(accounts);
        const firstValidatedAccount = accounts.find(
          (account: BankData) => account.isValidated,
        );
        if (firstValidatedAccount) {
          const formattedLabel = formatAccountLabel(firstValidatedAccount);
          setSelectedAccount(firstValidatedAccount.subaccountId);
          setSelectedAccountLabel(formattedLabel);
        }
      } catch (error) {
        setError("Failed to fetch bank accounts");
      }
    };

    fetchBankAccounts();
  }, []);

  const formatAccountLabel = (account: {
    accountNumber: string;
    bankName: string;
    accountName: string;
  }) =>
    `${account.accountNumber} - ${account.bankName} - ${account.accountName}`;
  const [isActive, setIsActive] = useState(false);

  const handleToggleActive = () => {
    setIsActive((prevState) => {
      const newState = !prevState;
      if (newState) {
        handlePaymentLink();
      }
      return newState;
    });
  };

  const handleSave = () => {
    setValidationResults({ reoccurringGroupName: "" });

    const errors: InvoiceErrors = {};

    if (!formData.reoccurringGroupName) {
      errors.reoccurringGroupName = "Frequency is required";
    }

    if (Object.keys(errors).length > 0) {
      setValidationResults(errors);
      return;
    }
    setFormData((prevFormData) => ({
      ...prevFormData,
      isReoccurring: true,
    }));

    closeForm();
  };

  return (
    <>
      {/* Desktop */}
      <div className="hidden lg:block md:block bg-white pl-6 pb-2 pt-6 pr-6 rounded-lg">
        <div className="flex justify-between">
          <h1 className="text-[24px] ">Edit Recurring Invoice</h1>
          <button
            aria-label="Close"
            className="flex items-center justify-center bg-[#F4F7FF] h-11 w-11 pr-[1em]"
            type="button"
            onClick={closeForm}
          >
            <CloseIcon color="#212121" />
          </button>
        </div>
        <div className="grid grid-cols-2 mt-2">
          <div>
            <label>End Date</label>
            <InputComponent
              type="date"
              value={formData.isReoccurringEndDate}
              className=""
              handleChange={handleInputChange}
              name="isReoccurringEndDate"
              restrictPreviousDates={true}
            />
          </div>
          <div className="flex mt-[2em] justify-end gap-2">
            <input
              type="checkbox"
              checked={!formData.isReoccurringEndDate}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setFormData((prevData) => ({
                  ...prevData,
                  isReoccurringEndDate: isChecked ? "" : "some_date_value",
                }));
              }}
              name="isReoccurringEndDate"
            />
            <label className="font-normal mt-[-0.2em]">Repeat non-stop</label>
          </div>
        </div>

        <div className="mt-[1em]">
          <label>Repeat</label>
          <div className="mt-2 w-full">
            <DropdownComponent
              options={dropDownRepeatedly}
              name="reoccurringGroupName"
              placeholder="Frequency"
              selectedValue={formData.reoccurringGroupName}
              onChange={handleDropDownChange}
              dropdownClassName="  w-full bg-[#fff]   max-h-[48px] cursor-pointer  rounded-[4px] placeholder:text-[#A4A4A4] border-1 border-[#A4A4A4] mt-[-0.3em] ml-[-0.3em] mb-[1em]"
              iconClassName="mr-[1.5em] lg:mr-[0.5em] biglg:mr-[-0.2em] xl:mr-[-0.3em]"
            />
          </div>
          {validationResults.reoccurringGroupName && (
            <div className="mt-2 ">
              <span className="text-red-500">
                {validationResults.reoccurringGroupName}
              </span>
            </div>
          )}
        </div>
        <div className="flex justify-between mt-[2em]">
          Attach Payment Link
          <div
            className={`w-16 h-8 p-1 rounded-[69.33px] border-[0.53px] border-[#044E97] flex items-center transition duration-200 ease-in ${
              isActive
                ? "bg-[#044E97] justify-end"
                : "bg-[#ffffff] justify-start"
            }`}
          >
            <div
              onClick={handleToggleActive}
              className={`${
                isActive ? "bg-[#ffffff]" : "bg-[#044E97]"
              } cursor-pointer w-6 h-6 rounded-full flex justify-center items-center`}
            >
              {isActive ? <ArrowLeft /> : <ArrowRight />}
            </div>
          </div>
        </div>

        <div className="flex  gap-[2em] ">
          <button
            className="bg-[#ffffff] border border-[#044E97] text-[16px]  h-[48px]  lg:w-[200px] #044E97focus:ring-[#044E97] rounded-md text-[#044E97] bigmd:mr-[3em] bigmd:w-[172px]  md:w-[172px] mt-[2em]  "
            onClick={closeForm}
          >
            Cancel
          </button>
          <button
            className="bg-[#044E97] text-[16px]  h-[48px] 540:mr-[5em] lg:w-[200px]  rounded-md text-[#ffffff] ml:[2em] md:mr-[1.8em] bigmd:mr-[3.6em] bigmd:w-[172px] mt-[2em]  md:w-[172px] lg:mr-[-0.5em]    xl:mr-[0.5em] mb-[2em]"
            onClick={handleSave}
            disabled={isLoading}
          >
            Save
          </button>
        </div>
      </div>

      {/* Mobile */}
      <div className="md:hidden">
        <div className="grid grid-cols-2">
          <div>
            <label className="text-[16px]">End Date</label>
            <InputComponent
              type="date"
              value={formData.isReoccurringEndDate}
              className=""
              handleChange={handleInputChange}
              name="isReoccurringEndDate"
              restrictPreviousDates={true}
            />
          </div>
          <div className="flex mt-[2em] justify-end gap-2">
            <input
              type="checkbox"
              checked={!formData.isReoccurringEndDate}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setFormData((prevData) => ({
                  ...prevData,
                  isReoccurringEndDate: isChecked ? "" : "some_date_value",
                }));
              }}
              name="isReoccurringEndDate"
            />
            <label className="font-normal text-[14px] mt-[-0.1em]">
              Repeat non-stop
            </label>
          </div>
        </div>

        <div className="mt-[1em]">
          <label className="text-[16px]">Repeat</label>
          <div className="mt-2 w-full">
            <DropdownComponent
              options={dropDownRepeatedly}
              placeholder="Frequency"
              selectedValue={formData.reoccurringGroupName}
              onChange={handleDropDownChange}
              dropdownClassName="  w-full bg-[#fff] lg:w-[300px]  max-h-[48px] cursor-pointer xl:w-[434px] rounded-[4px] placeholder:text-[#A4A4A4] border-1 border-[#A4A4A4] mt-[-0.3em] ml-[-0.3em] mb-[1em]"
              iconClassName="mr-[-0.2em] "
            />
          </div>
          {validationResults.reoccurringGroupName && (
            <div className="mt-2 ml-4">
              <span className="text-red-500">
                {validationResults.reoccurringGroupName}
              </span>
            </div>
          )}
        </div>
        <div className="flex justify-between mt-[2em] text-[16px]">
          Attach Payment Link
          <div
            className={`w-16 h-8 p-1 rounded-[69.33px] border-[0.53px] border-[#044E97] flex items-center transition duration-200 ease-in ${
              isActive
                ? "bg-[#044E97] justify-end"
                : "bg-[#ffffff] justify-start"
            }`}
          >
            <div
              onClick={handleToggleActive}
              className={`${
                isActive ? "bg-[#ffffff]" : "bg-[#044E97]"
              } cursor-pointer w-6 h-6 rounded-full flex justify-center items-center`}
            >
              {isActive ? <ArrowLeft /> : <ArrowRight />}
            </div>
          </div>
        </div>
        <div className="flex  gap-[2em] ">
          <button
            className="bg-[#ffffff] border border-[#044E97] text-[16px]  h-[48px]  w-[165px] #044E97focus:ring-[#044E97] rounded-md text-[#044E97] bigmd:mr-[3em] bigmd:w-[172px]  md:w-[172px] xl:w-[200px] mt-[2em]  "
            onClick={closeForm}
          >
            Cancel
          </button>
          <button
            className="bg-[#044E97] text-[16px]  h-[48px] 540:mr-[5em] w-[165px]  rounded-md text-[#ffffff] ml:[2em] md:mr-[1.8em] bigmd:mr-[3.6em] bigmd:w-[172px] mt-[2em] md:mt-[2.9em] xl:mt-[2em] md:w-[172px] lg:mr-[-0.5em]  lg:mt-[-2em] xl:w-[200px] xl:mr-[0.5em] mb-[2em]"
            onClick={handleSave}
            disabled={isLoading}
          >
            Save
          </button>
        </div>
      </div>
    </>
  );
};

export default RecurringPrompt;
